import { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';

import { formatDate } from '#common/utils';
import EnhancedTable, { EnhancedTableProps, EnhancedTableRow } from '#components/EnhancedTable';
import {
  ALL_TIME_RANGE_PRESET,
  TimeRangeValue,
  resolveDateRangeParam,
} from '#components/time-range.utils';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import {
  useGetCollectionsQuery,
  useDeleteCollectionMutation,
} from '#services/collection';
import {
  KEY_CODES,
} from '#utils/constants';
import URLSearchParamsEx from '#utils/url-ex';
import {
  setDateRange,
  setFilter,
  setOrder,
  setOrderBy,
  setPageIndex,
  setPageSize,
} from './list.slice';

interface CollectionRow {
  id: string | number;
  name: string;
  inactivated?: boolean;
  createdDate?: string;
  relatedProductCount?: number;
  [key: string]: unknown;
}

interface CollectionListSlice {
  dateRange: TimeRangeValue;
  filter: string;
  order: 'asc' | 'desc';
  orderBy: string;
  pageIndex: number;
  rows: EnhancedTableRow[];
  rowCount: number;
  pageSize: number;
}

export default function CollectionList() {
  const {
    showBackdrop,
    hideBackdrop,
    showDialog,
    hideDialog,
    setDialogData,

    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [deleteCollection] = useDeleteCollectionMutation();

  const filter = useSelector((state: { collectionList: CollectionListSlice }) => state.collectionList.filter);
  const dateRange = useSelector((state: { collectionList: CollectionListSlice }) => state.collectionList.dateRange);
  const order = useSelector((state: { collectionList: CollectionListSlice }) => state.collectionList.order);
  const orderBy = useSelector((state: { collectionList: CollectionListSlice }) => state.collectionList.orderBy);
  const pageIndex = useSelector((state: { collectionList: CollectionListSlice }) => state.collectionList.pageIndex);
  const rows = useSelector((state: { collectionList: CollectionListSlice }) => state.collectionList.rows);
  const rowCount = useSelector((state: { collectionList: CollectionListSlice }) => state.collectionList.rowCount);
  const pageSize = useSelector((state: { collectionList: CollectionListSlice }) => state.collectionList.pageSize);
  const [filterForQuery, setFilterForQuery] = useState(filter);
  const [isInitialMount, setIsInitialMount] = useState(true);

  const dateRangeParam = resolveDateRangeParam('createdDate', dateRange);

  const query = useGetCollectionsQuery({
    dateRange: dateRangeParam,
    order,
    orderBy,
    filter: filterForQuery,
    page: pageIndex,
    pageSize,
  }, {
    refetchOnMountOrArgChange: true,
  });

  // Restore state from URL parameters on component mount only
  useEffect(() => {
    if (!isInitialMount) return;

    const urlParams = new URLSearchParamsEx(location as unknown as Record<string, unknown>);
    if (urlParams.values.filter !== undefined) {
      dispatch(setFilter(urlParams.values.filter as string));
      setFilterForQuery(urlParams.values.filter as string);
    }
    if (urlParams.values.order !== undefined) {
      dispatch(setOrder(urlParams.values.order as string));
    }
    if (urlParams.values.orderBy !== undefined) {
      dispatch(setOrderBy(urlParams.values.orderBy as string));
    }
    if (urlParams.values.pageSize !== undefined) {
      dispatch(setPageSize(urlParams.values.pageSize as number));
    }

    setIsInitialMount(false);
  }, [location.search, isInitialMount, dispatch]);

  useEffect(() => {
    simpleSnackbar(query as unknown as Parameters<typeof simpleSnackbar>[0]);
  }, [query.error]);

  const tableHeaders: EnhancedTableProps['headers'] = [{
    id: 'name',
    label: 'Collection',
    renderer: (row, idx) => {
      const collection = row as unknown as CollectionRow;
      const collectionIndex = pageIndex * pageSize + idx + 1;
      const qs = new URLSearchParamsEx({
        order,
        orderBy,
        filter,
        pageSize,
        collectionId: collection.id,
        collectionIndex,
        // The detail page re-runs the list query to resolve the row index (and
        // to walk prev/next), so it needs the same date window the list used —
        // without it the index points into the unfiltered list.
        ...(dateRangeParam ? { dateRange: dateRangeParam } : {}),
      }).toString();
      const url = `/management/collection/edit?${qs}`;
      return (
        <Box>
          <Link
            href={url}
            onClick={(e) => {
              e.preventDefault();
              void navigate(url);
            }}
          >
            {collection.name}
          </Link>
        </Box>
      );
    },
  }, {
    id: 'relatedProductCount',
    label: 'Items',
  }, {
    id: 'createdDate',
    label: 'Created',
    renderer: (row) => formatDate((row as unknown as CollectionRow).createdDate ?? '', '-'),
  }, {
    id: 'inactivated',
    label: 'Status',
    renderer: (row) => ((row as unknown as CollectionRow).inactivated ? <Chip size="small" label="Inactive" color="warning" /> : <Chip size="small" label="Active" color="success" />),
  }, {
    id: 'action',
    label: 'Action',
    sortable: false,
    renderer: (row) => {
      const collection = row as unknown as CollectionRow;
      const handleDeleteCollection = () => {
        setDialogData({
          title: 'Delete',
          text: 'Are you sure you want to delete this collection?',
          disagree: 'Cancel',
          agree: 'Delete',
          onDisagree: () => hideDialog(),
          onAgree: () => {
            hideDialog();
            showBackdrop();

            void deleteCollection({
              order,
              orderBy,
              filter,
              page: pageIndex,
              pageSize,
              collectionId: collection.id,
            }).then((res) => {
              simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], `Collection "${collection.name}" was successfully deleted.`);
            }).finally(() => {
              hideBackdrop();
            });
          },
        });
        showDialog();
      };

      const disabled = ((collection.relatedProductCount || 0) > 0);
      if (disabled) {
        return (
          <Box>
            <Tooltip title="Deletion of a collection is only possible when it contains no products.">
              <span>
                <Link
                  component="button"
                  className="disabled-link"
                >
                  Delete
                </Link>
              </span>
            </Tooltip>
          </Box>
        );
      }

      return (
        <Box>
          <Link
            component="button"
            onClick={handleDeleteCollection}
          >
            Delete
          </Link>
        </Box>
      );
    },
  }];

  const tableProps: EnhancedTableProps = {
    name: 'variants',

    headers: tableHeaders,

    enableSort: true,

    order,
    setOrder,
    orderBy,
    setOrderBy,

    pageSize,
    pageIndex,
    setPageIndex,
    setPageSize,
    rowCount,

    timeRange: {
      value: dateRange,
      onChange: (value) => dispatch(setDateRange(value)),
      columnLabel: 'Created',
      defaultPreset: ALL_TIME_RANGE_PRESET,
      entityLabel: 'collections',
    },

    rows,
  };

  return (
    <>
      <Box sx={{ typography: 'h7' }}>
        Collections
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <Paper
          elevation={0}
          variant="outlined"
          square
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: 400,
          }}
        >
          <IconButton type="button" sx={{ p: '10px' }} aria-label="search">
            <SearchIcon />
          </IconButton>
          <InputBase
            sx={{ ml: 1, flex: 1 }}
            placeholder="Filter Collections"
            value={filter}
            onChange={(e) => {
              dispatch(setFilter(e.target.value));
            }}
            onKeyUp={(e) => {
              if (e.keyCode === KEY_CODES.ENTER || !filter.length) {
                setFilterForQuery(filter);
              }
            }}
          />
        </Paper>
      </Box>

      <EnhancedTable
        {...tableProps}
      />
    </>
  );
}
